/**
 * Contextual tooltip (FR-HELP-04, AC-HELP-004).
 *
 * One shared component for the whole app. It renders its own trigger — a small
 * information button sized to the 44px WCAG floor — rather than wrapping the
 * control it explains, so a tooltip can sit next to any label without the
 * caller giving up control of its own markup or ARIA.
 *
 * Behaviour, per the help spec:
 *   - hover opens after {@link TOOLTIP_HOVER_DELAY_MS} (500ms), so a pointer
 *     crossing the form does not flash a dozen bubbles;
 *   - keyboard focus opens immediately — a keyboard user has already committed;
 *   - Escape dismisses without touching form state (the key press is stopped
 *     before it can reach a parent dialog);
 *   - the bubble is linked to the trigger with `aria-describedby` while open.
 *
 * Text resolution is two-layer: the shipped registry
 * (`src/registry/tooltips.json`) is the default, and runtime overrides fetched
 * from `GET /help/v1/tooltips` are laid on top through
 * {@link applyTooltipOverrides}. An unknown key renders nothing at all rather
 * than an empty bubble.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import registryFile from '../registry/tooltips.json';

/**
 * Subtree switch that turns every {@link Tooltip} inside it into `null`.
 *
 * Play Entry on a laptop is a speed surface: a help button beside each of ~25
 * labels is visual noise a mouse user never needs between snaps, so the page
 * suppresses inline tooltips at the desktop breakpoint and points to its
 * dedicated help screen instead (`/app/{tenant}/play-entry/help`), which lists
 * the very same registry text. Tablet and phone keep the tap/focus tooltips.
 */
const TooltipSuppressionContext = createContext(false);

export function TooltipSuppression({
  isSuppressed,
  children,
}: {
  isSuppressed: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <TooltipSuppressionContext.Provider value={isSuppressed}>
      {children}
    </TooltipSuppressionContext.Provider>
  );
}

/** Are inline tooltips switched off for this subtree? */
export function useIsTooltipSuppressed(): boolean {
  return useContext(TooltipSuppressionContext);
}

export interface TooltipEntry {
  /** Human name of the control, used for the trigger's accessible name. */
  label: string;
  text: string;
  surface?: string;
  /** Retired keys stay in the registry as tombstones and never render. */
  retired?: boolean;
}

export type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

/** Hover dwell before a tooltip opens. Focus is immediate. */
export const TOOLTIP_HOVER_DELAY_MS = 500;

const SHIPPED_TOOLTIPS: Record<string, TooltipEntry> = registryFile.entries;

/** Runtime (tenant/platform) overrides, keyed by the same tooltipId. */
let tooltipOverrides: Record<string, string> = {};
/** Bumped on every override swap so mounted tooltips re-resolve their text. */
let overrideRevision = 0;
const overrideListeners = new Set<() => void>();

function subscribeToOverrides(listener: () => void): () => void {
  overrideListeners.add(listener);
  return () => overrideListeners.delete(listener);
}

/**
 * Replace the runtime override layer, e.g. after `GET /help/v1/tooltips`
 * resolves. Passing `{}` restores the shipped defaults. Mounted tooltips
 * re-render through the subscription below — the layer is module state on
 * purpose, so a tooltip deep in a form needs no provider or prop drilling.
 */
export function applyTooltipOverrides(overrides: Record<string, string>): void {
  tooltipOverrides = { ...overrides };
  overrideRevision += 1;
  for (const listener of overrideListeners) listener();
}

/** Minimal slice of the API client this component needs. */
export interface TooltipOverrideLoader {
  (signal: AbortSignal): Promise<{ tooltips: Record<string, string> }>;
}

/**
 * Hydrate the override layer once per mount from `GET /help/v1/tooltips`
 * (tenant > platform > shipped default, resolved server-side). Mounted in the
 * authenticated shell, so every page gets the tenant's wording.
 *
 * A failure is deliberately silent: the shipped registry is a complete, correct
 * fallback, and a help-service outage must never surface as an error banner on
 * a coaching screen.
 */
export function useTooltipOverrides(load: TooltipOverrideLoader): void {
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) applyTooltipOverrides(response.tooltips);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [load]);
}

/** Every shipped key, tombstones excluded — used by the help admin surfaces. */
export function tooltipRegistryKeys(): readonly string[] {
  return Object.entries(SHIPPED_TOOLTIPS)
    .filter(([, entry]) => entry.retired !== true)
    .map(([key]) => key);
}

/** Shipped default with any runtime override applied. `null` when unknown. */
export function resolveTooltip(tooltipId: string): TooltipEntry | null {
  const shipped = SHIPPED_TOOLTIPS[tooltipId];
  if (shipped === undefined || shipped.retired === true) return null;
  const override = tooltipOverrides[tooltipId];
  return override === undefined || override === '' ? shipped : { ...shipped, text: override };
}

export interface TooltipProps {
  /** Stable registry key, e.g. `play_entry.quarter`. */
  tooltipId: string;
  position?: TooltipPosition;
  /** Overrides the registry label in the trigger's accessible name. */
  label?: string;
}

export function Tooltip({ tooltipId, position = 'top', label }: TooltipProps): JSX.Element | null {
  const isSuppressed = useIsTooltipSuppressed();
  // Re-resolve when the override layer changes (help service answered late).
  useSyncExternalStore(
    subscribeToOverrides,
    () => overrideRevision,
    () => overrideRevision
  );
  const entry = isSuppressed ? null : resolveTooltip(tooltipId);
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bubbleId = `tooltip-${useId()}`;

  const cancelHover = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelHover();
    setIsOpen(false);
  }, [cancelHover]);

  // Escape closes the bubble and stops there: a tooltip inside a dialog must
  // not also close the dialog, and no form state is touched either way.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (keyEvent: KeyboardEvent): void => {
      if (keyEvent.key !== 'Escape') return;
      keyEvent.stopPropagation();
      close();
    };
    globalThis.addEventListener?.('keydown', onKeyDown, true);
    return () => globalThis.removeEventListener?.('keydown', onKeyDown, true);
  }, [close, isOpen]);

  useEffect(() => cancelHover, [cancelHover]);

  if (entry === null) return null;
  const accessibleName = `Help: ${label ?? entry.label}`;

  return (
    <span
      className="tooltip"
      onPointerEnter={() => {
        cancelHover();
        timerRef.current = setTimeout(() => setIsOpen(true), TOOLTIP_HOVER_DELAY_MS);
      }}
      onPointerLeave={close}
    >
      <button
        type="button"
        className="tooltip__trigger"
        aria-label={accessibleName}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? bubbleId : undefined}
        onFocus={() => setIsOpen(true)}
        onBlur={close}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span aria-hidden="true">i</span>
      </button>
      {isOpen ? (
        <span
          className={`tooltip__bubble tooltip__bubble--${position}`}
          id={bubbleId}
          role="tooltip"
        >
          {entry.text}
        </span>
      ) : null}
    </span>
  );
}
